import React, { createContext, useState, useReducer, useMemo } from "react";
import { GroupSeries, Report, Query, stringifyQuery, groupSeriesFor, groupBarSeriesFor } from "../lib/api/charts";
import { Loading } from "./base";

type GroupSeriesSet = { series: GroupSeries; loading: Loading; measure?: any };

export type GroupChartContextState = {
  series: {
    [index: string]: GroupSeriesSet;
  };
  getGroupSeries(report: Report, query: Query, measure?: any): Partial<GroupSeriesSet>;
  getGroupBarSeries(report: Report, query: Query, measure?: any): Partial<GroupSeriesSet>;
};

export const GroupChartContext = createContext<GroupChartContextState>({
  series: {},
  getGroupSeries: () => ({}),
  getGroupBarSeries: () => ({}),
});

type QueryAction = {
  type: "PATCH_SERIES";
  id: string;
  data: Partial<GroupSeriesSet>;
};

const defaultGroupSeries: GroupSeriesSet = {
  series: { name: "", rows: [], classification: "income", type: "currency", dealers: [], formats: { pos: "", neg: "" } },
  loading: { loaded: false, loading: true, error: null },
  measure: {},
};

export const GroupChartProvider = ({ children }) => {
  const memo = (report: Report, query: Query, measure?: any) => `${report}-${stringifyQuery(query)}-${JSON.stringify(measure)}`;

  const [series, dispatchGroupSeries] = useReducer((state: { [index: string]: GroupSeriesSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_SERIES":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      series,
      getGroupSeries: (report: Report, query: Query, measure?: any) => {
        const id = memo(report, query, measure);
        const set = series[id];

        if (set) {
          return set;
        }

        dispatchGroupSeries({
          type: "PATCH_SERIES",
          id,
          data: defaultGroupSeries,
        });

        groupSeriesFor(query, report, measure)
          .then(series => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                series: series,
                measure,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                loading: {
                  loaded: false,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultGroupSeries;
      },
      getGroupBarSeries: (report: Report, query: Query, measure?: any) => {
        const id = memo(report, query, measure);
        const set = series[id];

        if (set) {
          return set;
        }

        dispatchGroupSeries({
          type: "PATCH_SERIES",
          id,
          data: defaultGroupSeries,
        });

        groupBarSeriesFor(query, report, measure)
          .then(series => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                series: series,
                measure,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchGroupSeries({
              type: "PATCH_SERIES",
              id,
              data: {
                loading: {
                  loaded: false,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultGroupSeries;
      },
    }),
    [series],
  );

  return <GroupChartContext.Provider value={value}>{children}</GroupChartContext.Provider>;
};
